import { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  Phone,
  Check,
  RefreshCw,
  Search,
  MessageSquare,
  PhoneForwarded,
  AlertTriangle,
  Sparkles,
  ChevronRight,
  Wallet,
  CreditCard,
  Plus,
  PauseCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabaseClient';
import {
  getNexusPhoneStatus,
  searchNexusNumbers,
  provisionNexusNumber,
  releaseNexusNumber,
  addNexusPhoneFunds,
  confirmNexusPhoneCheckout,
  setNexusPhoneAutoTopup,
  fetchNexusPhoneTransactions,
  formatMoney,
  formatUnitPrice,
  formatNexusPhone,
  NexusPhoneError,
  type AvailableNexusNumber,
  type NexusPhoneStatusResponse,
  type NexusPhoneTransaction,
  type NexusPhonePricing,
  type NexusSearchMatchedBy,
} from '../../lib/nexusPhone';
import CityAutocompleteInput, { type SelectedCity } from '../CityAutocompleteInput';

interface NexusPhoneNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after a number is added, released, or forwarding changes. */
  onChanged?: () => void;
  /** Lets the "own Twilio connected" blocker jump straight to the Twilio settings. */
  onOpenTwilioSettings?: () => void;
}

type Country = 'CA' | 'US';

const COUNTRY_LABEL: Record<Country, string> = { CA: 'Canada', US: 'United States' };
const PENDING_NUMBER_KEY = 'nexus_phone_pending_number';
const TOPUP_PRESETS_CENTS = [2000, 5000, 10000];
const THRESHOLD_OPTIONS_CENTS = [500, 1000, 2500, 5000];
const AUTO_AMOUNT_OPTIONS_CENTS = [2000, 5000, 10000, 20000];

function formatToE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
}

function cardLabel(card: { brand: string | null; last4: string | null } | null): string | null {
  if (!card) return null;
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  return card.last4 ? `${brand} •••• ${card.last4}` : brand;
}

export default function NexusPhoneNumberModal({ isOpen, onClose, onChanged, onOpenTwilioSettings }: NexusPhoneNumberModalProps) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<NexusPhoneStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<NexusPhoneTransaction[]>([]);

  // Picker
  // Canada only for now; US will come back once A2P 10DLC registration is handled.
  const country: Country = 'CA';
  const [cityText, setCityText] = useState('');
  const [city, setCity] = useState<SelectedCity | null>(null);
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState<AvailableNexusNumber[]>([]);
  const [resultsCountry, setResultsCountry] = useState<string | null>(null);
  const [resultsMatchedBy, setResultsMatchedBy] = useState<NexusSearchMatchedBy | null>(null);
  const [resultsCity, setResultsCity] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const seenRef = useRef<Set<string>>(new Set());

  // Confirm + fund + provision
  const [selected, setSelected] = useState<AvailableNexusNumber | null>(null);
  const [initialAmountCents, setInitialAmountCents] = useState(2000);
  // Entering the confirm step: a starting balance is required when the wallet is
  // empty, but purely optional (default $0) when it already covers the minimum.
  useEffect(() => {
    if (!selected || !status) return;
    const needs = status.wallet.balanceCents < status.pricing.minBalanceToProvisionCents;
    setInitialAmountCents(needs ? 2000 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.phoneNumber]);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [autoThresholdCents, setAutoThresholdCents] = useState(500);
  const [autoAmountCents, setAutoAmountCents] = useState(2000);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionStep, setProvisionStep] = useState<string | null>(null);

  // Connected: add funds / auto top-up editing
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [addAmountCents, setAddAmountCents] = useState(2000);
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [editingAuto, setEditingAuto] = useState(false);
  const [isSavingAuto, setIsSavingAuto] = useState(false);

  // Release
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);

  // Call forwarding
  const [editingForwarding, setEditingForwarding] = useState(false);
  const [fwdEnabled, setFwdEnabled] = useState(false);
  const [fwdNumber, setFwdNumber] = useState('');
  const [isSavingForwarding, setIsSavingForwarding] = useState(false);

  const checkoutHandledRef = useRef(false);

  const load = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [s, tx] = await Promise.all([getNexusPhoneStatus(businessId), fetchNexusPhoneTransactions(businessId, 8)]);
      setStatus(s);
      setTransactions(tx);
      setFwdEnabled(s.twilio.callForwardingEnabled);
      setFwdNumber(s.twilio.forwardingNumber || '');
      setAutoEnabled(s.wallet.autoTopup.enabled);
      setAutoThresholdCents(s.wallet.autoTopup.thresholdCents || 500);
      setAutoAmountCents(s.wallet.autoTopup.amountCents || 2000);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load phone number status');
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  const refreshQuiet = useCallback(async () => {
    if (!businessId) return;
    try {
      const [s, tx] = await Promise.all([getNexusPhoneStatus(businessId), fetchNexusPhoneTransactions(businessId, 8)]);
      setStatus(s);
      setTransactions(tx);
    } catch {
      /* keep what we have */
    }
  }, [businessId]);

  const doProvision = useCallback(
    async (n: AvailableNexusNumber): Promise<boolean> => {
      if (!businessId) return false;
      setProvisionStep('Activating your number…');
      try {
        await provisionNexusNumber({
          businessId,
          phoneNumber: n.phoneNumber,
          country: n.country,
          locality: n.locality,
          region: n.region,
        });
        toast.success(`${formatNexusPhone(n.phoneNumber)} is now your business number`);
        sessionStorage.removeItem(PENDING_NUMBER_KEY);
        setSelected(null);
        setResults([]);
        onChanged?.();
        return true;
      } catch (e) {
        const err = e as NexusPhoneError;
        toast.error(err.message || 'Could not get that number');
        if (err.code === 'number_unavailable') {
          setResults((prev) => prev.filter((x) => x.phoneNumber !== n.phoneNumber));
          setSelected(null);
          sessionStorage.removeItem(PENDING_NUMBER_KEY);
        } else if (err.code === 'already_provisioned' || err.code === 'own_twilio_connected') {
          setSelected(null);
          sessionStorage.removeItem(PENDING_NUMBER_KEY);
        }
        return false;
      } finally {
        setProvisionStep(null);
      }
    },
    [businessId, onChanged],
  );

  /** Where Stripe Checkout should send the user back to. Like the other
   *  integrations, that's the App Store's Integrations tab — never the legacy
   *  standalone /integrations page. Elsewhere (e.g. Messages) return in place. */
  const checkoutReturnUrl = (flag: 'cancelled' | '1') => {
    const path = window.location.pathname || '/app-store';
    const backToStore = path.startsWith('/app-store') || path.startsWith('/integrations');
    const url = new URL(backToStore ? '/app-store' : path, window.location.origin);
    if (backToStore) url.searchParams.set('tab', 'integrations');
    url.searchParams.set('nexus_phone_checkout', flag);
    return url.toString();
  };

  // Returning from Stripe Checkout (no card on file / card declined path).
  const handleCheckoutReturn = useCallback(async () => {
    if (!businessId || checkoutHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('nexus_phone_checkout');
    if (!flag) return;
    checkoutHandledRef.current = true;

    const cleanUrl = () => {
      params.delete('nexus_phone_checkout');
      params.delete('session_id');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    };

    const pendingRaw = sessionStorage.getItem(PENDING_NUMBER_KEY);
    let pending: AvailableNexusNumber | null = null;
    try { pending = pendingRaw ? (JSON.parse(pendingRaw) as AvailableNexusNumber) : null; } catch { pending = null; }

    if (flag === 'cancelled') {
      cleanUrl();
      if (pending) setSelected(pending);
      toast('Payment cancelled — your number is still reserved in this screen.', { icon: 'ℹ️' });
      return;
    }

    const sessionId = params.get('session_id');
    if (!sessionId) { cleanUrl(); return; }
    try {
      const res = await confirmNexusPhoneCheckout({ businessId, sessionId });
      toast.success(`Added to your phone balance — now ${formatMoney(res.wallet.balanceCents)}`);
      cleanUrl();
      if (pending) {
        setIsProvisioning(true);
        await doProvision(pending);
        setIsProvisioning(false);
      }
    } catch (e) {
      cleanUrl();
      toast.error(e instanceof Error ? e.message : 'Could not confirm your payment');
      if (pending) setSelected(pending);
    }
  }, [businessId, doProvision]);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setConfirmRelease(false);
    setEditingForwarding(false);
    setEditingAuto(false);
    setAddFundsOpen(false);
    setResults([]);
    setHasSearched(false);
    seenRef.current = new Set();
    void (async () => {
      await handleCheckoutReturn();
      await load();
    })();
  }, [isOpen, load, handleCheckoutReturn]);

  const handleSearch = async (refresh = false) => {
    if (!businessId) return;
    setIsSearching(true);
    try {
      if (!refresh) seenRef.current = new Set();
      const data = await searchNexusNumbers({
        businessId,
        country,
        city: city?.city || undefined,
        region: city?.state || undefined,
        lat: city?.lat,
        lng: city?.lng,
        areaCode: areaCode || undefined,
      });
      const all = data.numbers || [];
      let next = all;
      if (refresh) {
        const unseen = all.filter((n) => !seenRef.current.has(n.phoneNumber));
        if (unseen.length > 0) next = unseen;
        else if (all.length > 0) toast('No new numbers right now — showing the current batch.');
      }
      next.forEach((n) => seenRef.current.add(n.phoneNumber));
      setResults(next);
      setResultsCountry(data.country || country);
      setResultsMatchedBy(data.matchedBy || null);
      setResultsCity(city?.city || null);
      setHasSearched(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Number search failed');
    } finally {
      setIsSearching(false);
    }
  };

  /** Confirm step: save auto top-up → fund (card or Checkout) → buy the number. */
  const handleFundAndProvision = async () => {
    if (!businessId || !selected || !status) return;
    const needsFunds = status.wallet.balanceCents < status.pricing.minBalanceToProvisionCents;
    const amount = needsFunds || initialAmountCents > 0 ? initialAmountCents : 0;
    if (needsFunds && amount < status.pricing.minTopupCents) {
      toast.error(`Add at least ${formatMoney(status.pricing.minTopupCents)} to get started.`);
      return;
    }
    setIsProvisioning(true);
    try {
      setProvisionStep('Saving auto top-up…');
      await setNexusPhoneAutoTopup({ businessId, enabled: autoEnabled, thresholdCents: autoThresholdCents, amountCents: autoAmountCents });

      if (amount > 0) {
        setProvisionStep('Adding funds…');
        const res = await addNexusPhoneFunds({
          businessId,
          amountCents: amount,
          source: 'initial',
          successUrl: checkoutReturnUrl('1'),
          cancelUrl: checkoutReturnUrl('cancelled'),
        });
        if (!res.charged) {
          // Off to Stripe Checkout; finish provisioning when we come back.
          sessionStorage.setItem(PENDING_NUMBER_KEY, JSON.stringify(selected));
          window.location.href = res.checkoutUrl;
          return;
        }
        toast.success(`${formatMoney(amount)} added to your phone balance`);
      }

      const ok = await doProvision(selected);
      await load();
      if (!ok) await refreshQuiet();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Something went wrong');
      await refreshQuiet();
    } finally {
      setIsProvisioning(false);
      setProvisionStep(null);
    }
  };

  const handleAddFunds = async () => {
    if (!businessId || !status) return;
    if (addAmountCents < status.pricing.minTopupCents) {
      toast.error(`The minimum top-up is ${formatMoney(status.pricing.minTopupCents)}.`);
      return;
    }
    setIsAddingFunds(true);
    try {
      const res = await addNexusPhoneFunds({
        businessId,
        amountCents: addAmountCents,
        successUrl: checkoutReturnUrl('1'),
        cancelUrl: checkoutReturnUrl('cancelled'),
      });
      if (!res.charged) {
        window.location.href = res.checkoutUrl;
        return;
      }
      toast.success(`${formatMoney(addAmountCents)} added — balance is now ${formatMoney(res.wallet.balanceCents)}`);
      if (res.reactivated) toast.success('Texting and calling are back on.');
      setAddFundsOpen(false);
      await refreshQuiet();
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add funds');
    } finally {
      setIsAddingFunds(false);
    }
  };

  const handleSaveAuto = async () => {
    if (!businessId) return;
    setIsSavingAuto(true);
    try {
      const res = await setNexusPhoneAutoTopup({ businessId, enabled: autoEnabled, thresholdCents: autoThresholdCents, amountCents: autoAmountCents });
      setStatus((prev) => (prev ? { ...prev, wallet: res.wallet } : prev));
      setEditingAuto(false);
      toast.success(autoEnabled ? 'Auto top-up saved' : 'Auto top-up turned off');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save auto top-up');
    } finally {
      setIsSavingAuto(false);
    }
  };

  const handleRelease = async () => {
    if (!businessId) return;
    setIsReleasing(true);
    try {
      const res = await releaseNexusNumber(businessId);
      toast.success('Phone number released');
      (res.warnings || []).forEach((w) => toast(w, { icon: '⚠️', duration: 8000 }));
      setConfirmRelease(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not release the number');
    } finally {
      setIsReleasing(false);
    }
  };

  const handleSaveForwarding = async () => {
    if (!businessId) return;
    const formatted = fwdEnabled ? formatToE164(fwdNumber) : null;
    if (fwdEnabled && (!formatted || formatted.replace(/\D/g, '').length < 10)) {
      toast.error('Enter the phone number calls should forward to.');
      return;
    }
    setIsSavingForwarding(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ twilio_call_forwarding_enabled: fwdEnabled, twilio_forwarding_number: formatted })
        .eq('id', businessId);
      if (error) throw error;
      setStatus((prev) =>
        prev
          ? { ...prev, twilio: { ...prev.twilio, callForwardingEnabled: fwdEnabled, forwardingNumber: formatted } }
          : prev,
      );
      setEditingForwarding(false);
      toast.success('Call forwarding updated');
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save call forwarding');
    } finally {
      setIsSavingForwarding(false);
    }
  };

  if (!isOpen) return null;

  const number = status?.number ?? null;
  const wallet = status?.wallet ?? null;
  const pricing = status?.pricing ?? null;
  const blockers = status?.blockers ?? [];
  const ownTwilio = blockers.find((b) => b.code === 'own_twilio_connected');
  const subBlocker = blockers.find((b) => b.code === 'no_subscription' || b.code === 'subscription_inactive');
  const card = cardLabel(status?.card ?? null);
  const pausedNoFunds = number?.status === 'suspended' && number.suspended_reason === 'no_funds';
  const needsFunds = !!status && status.wallet.balanceCents < status.pricing.minBalanceToProvisionCents;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Nexus Phone Number</h2>
                <p className="text-sm text-gray-400 mt-1">A business number for texting and calling — set up in one click</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : loadError ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {loadError}
              <button type="button" onClick={() => void load()} className="ml-2 font-semibold underline">
                Retry
              </button>
            </div>
          ) : !status || !wallet || !pricing ? null : number ? (
            /* ─────────── Connected / paused ─────────── */
            <div className="space-y-5">
              <div className={`p-5 rounded-2xl border ${number.status === 'active' ? 'bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your business number</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1 font-mono tracking-tight">{formatNexusPhone(number.phone_number)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {[number.locality, number.region].filter(Boolean).join(', ') || COUNTRY_LABEL[(number.country as Country)] || number.country}
                      {' · '}since {new Date(number.provisioned_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  {number.status === 'active' ? (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <Check className="h-3.5 w-3.5" /> Active
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                      <PauseCircle className="h-3.5 w-3.5" /> Paused
                    </span>
                  )}
                </div>

                {pausedNoFunds ? (
                  <p className="text-sm text-amber-800 mt-3">
                    Texting and calling are paused because your phone balance ran out. Add funds below and the number
                    comes back on right away.
                  </p>
                ) : number.status === 'suspended' ? (
                  <p className="text-sm text-amber-800 mt-3">This number is paused. Contact support if you weren't expecting this.</p>
                ) : null}
              </div>

              {/* Balance */}
              <div className="p-5 bg-white border border-gray-200 rounded-2xl space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 inline-flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5" /> Phone balance
                    </p>
                    <p className={`text-3xl font-bold mt-1 ${wallet.balanceCents <= 0 ? 'text-red-600' : wallet.balanceCents < wallet.autoTopup.thresholdCents ? 'text-amber-600' : 'text-gray-900'}`}>
                      {formatMoney(wallet.balanceCents)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Texts and calls are deducted as you go · {formatMoney(wallet.totalSpentCents)} used so far
                    </p>
                  </div>
                  {!addFundsOpen && (
                    <button
                      type="button"
                      onClick={() => setAddFundsOpen(true)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Plus className="h-4 w-4" /> Add funds
                    </button>
                  )}
                </div>

                {addFundsOpen && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                    <AmountPicker value={addAmountCents} onChange={setAddAmountCents} min={pricing.minTopupCents} max={pricing.maxTopupCents} />
                    <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                      <CreditCard className="h-3.5 w-3.5" />
                      {card ? `Charged to ${card}` : 'You’ll be asked for a card on the next screen'}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleAddFunds}
                        disabled={isAddingFunds}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold"
                      >
                        {isAddingFunds ? 'Adding…' : `Add ${formatMoney(addAmountCents)}`}
                      </button>
                      <button type="button" onClick={() => setAddFundsOpen(false)} disabled={isAddingFunds} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Auto top-up */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Auto top-up</p>
                    {!editingAuto && (
                      <button
                        type="button"
                        onClick={() => {
                          setAutoEnabled(wallet.autoTopup.enabled);
                          setAutoThresholdCents(wallet.autoTopup.thresholdCents || 500);
                          setAutoAmountCents(wallet.autoTopup.amountCents || 2000);
                          setEditingAuto(true);
                        }}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        {wallet.autoTopup.enabled ? 'Edit' : 'Turn on'}
                      </button>
                    )}
                  </div>
                  {editingAuto ? (
                    <div className="mt-2 space-y-3">
                      <AutoTopupForm
                        enabled={autoEnabled}
                        thresholdCents={autoThresholdCents}
                        amountCents={autoAmountCents}
                        onChange={(v) => { setAutoEnabled(v.enabled); setAutoThresholdCents(v.thresholdCents); setAutoAmountCents(v.amountCents); }}
                      />
                      <div className="flex gap-2">
                        <button type="button" onClick={handleSaveAuto} disabled={isSavingAuto} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold">
                          {isSavingAuto ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setEditingAuto(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <p className="text-sm text-gray-600">
                        {wallet.autoTopup.enabled ? (
                          <>
                            <span className="text-green-600 font-medium">On</span> · when the balance drops below{' '}
                            <span className="font-semibold">{formatMoney(wallet.autoTopup.thresholdCents)}</span>, add{' '}
                            <span className="font-semibold">{formatMoney(wallet.autoTopup.amountCents)}</span>
                            {card ? ` from ${card}` : ''}
                          </>
                        ) : (
                          <span className="text-gray-400">Off — texting and calling pause when the balance hits $0</span>
                        )}
                      </p>
                      {wallet.autoTopup.pausedReason === 'payment_failed' && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex gap-2">
                          <AlertTriangle className="h-4 w-4 shrink-0" />
                          <span>
                            The last auto top-up failed{wallet.autoTopup.lastError ? `: ${wallet.autoTopup.lastError}` : '.'} Update your card or add funds
                            manually — saving auto top-up again turns it back on.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <RatesCard pricing={pricing} country={(number.country as Country) || 'CA'} />

              {/* Recent activity */}
              {transactions.length > 0 && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 mb-2">Recent activity</p>
                  <ul className="divide-y divide-gray-200">
                    {transactions.map((t) => (
                      <li key={t.id} className="py-1.5 flex items-center justify-between gap-3 text-xs">
                        <div className="min-w-0">
                          <p className="text-gray-800 truncate">{t.description || t.type}</p>
                          <p className="text-gray-400">{new Date(t.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`font-semibold ${t.amount_cents >= 0 ? 'text-green-700' : 'text-gray-900'}`}>
                            {t.amount_cents >= 0 ? '+' : '−'}{formatMoney(Math.abs(t.amount_cents))}
                          </p>
                          <p className="text-gray-400">bal. {formatMoney(t.balance_after_cents)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Call forwarding */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <PhoneForwarded className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Call forwarding</span>
                  </div>
                  {!editingForwarding && (
                    <button
                      type="button"
                      onClick={() => {
                        setFwdEnabled(status.twilio.callForwardingEnabled);
                        setFwdNumber(status.twilio.forwardingNumber || '');
                        setEditingForwarding(true);
                      }}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingForwarding ? (
                  <div className="space-y-3 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={fwdEnabled}
                        onChange={(e) => setFwdEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Forward incoming calls to another phone</span>
                    </label>
                    {fwdEnabled && (
                      <input
                        type="tel"
                        value={fwdNumber}
                        onChange={(e) => setFwdNumber(e.target.value)}
                        placeholder="+1 604 555 0123"
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveForwarding}
                        disabled={isSavingForwarding}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold"
                      >
                        {isSavingForwarding ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" onClick={() => setEditingForwarding(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    {status.twilio.callForwardingEnabled && status.twilio.forwardingNumber ? (
                      <>
                        <span className="text-green-600 font-medium">On</span> · forwarding to{' '}
                        <span className="font-mono">{formatNexusPhone(status.twilio.forwardingNumber)}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">Off — calls ring in the Nexus dialler</span>
                    )}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <MessageSquare className="h-3.5 w-3.5" />
                Use it from <span className="font-medium text-gray-700">Messages</span>, the <span className="font-medium text-gray-700">Dialler</span>, automations, and your AI receptionist.
              </div>

              {/* Release */}
              <div className="pt-2 border-t border-gray-200">
                {confirmRelease ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
                    <p className="text-sm text-red-800">
                      Release <span className="font-mono font-semibold">{formatNexusPhone(number.phone_number)}</span>? You'll lose this
                      number for good and texting and calling will stop. Your remaining balance stays on your account.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleRelease}
                        disabled={isReleasing}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold"
                      >
                        {isReleasing ? 'Releasing…' : 'Yes, release it'}
                      </button>
                      <button type="button" onClick={() => setConfirmRelease(false)} disabled={isReleasing} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                        Keep my number
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setConfirmRelease(true)} className="text-xs font-semibold text-red-600 hover:text-red-700">
                    Release this number
                  </button>
                )}
              </div>
            </div>
          ) : status.configError ? (
            /* ─────────── Not configured (platform side) ─────────── */
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <p className="font-semibold mb-1">Nexus phone numbers aren't available yet.</p>
              <p className="text-xs">{status.configError}</p>
            </div>
          ) : blockers.length > 0 ? (
            /* ─────────── Blocked ─────────── */
            <div className="space-y-4">
              <IntroCard pricing={pricing} />
              {ownTwilio && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-800">{ownTwilio.message}</p>
                  {onOpenTwilioSettings && (
                    <button type="button" onClick={onOpenTwilioSettings} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                      Open Twilio settings <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
              {subBlocker && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-800">{subBlocker.message}</p>
                  <a href="/my-apps" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                    Go to plans &amp; billing <ChevronRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
              {blockers
                .filter((b) => b !== ownTwilio && b !== subBlocker)
                .map((b) => (
                  <div key={b.code} className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800">
                    {b.message}
                  </div>
                ))}
            </div>
          ) : selected ? (
            /* ─────────── Confirm: fund + get number ─────────── */
            <div className="space-y-5">
              <div className="p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">You picked</p>
                <p className="text-2xl font-bold text-gray-900 mt-1 font-mono tracking-tight">{formatNexusPhone(selected.phoneNumber)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {[selected.locality, selected.region].filter(Boolean).join(', ') || 'Local number'} · {COUNTRY_LABEL[(selected.country as Country)] || selected.country}
                </p>
              </div>

              <RatesCard pricing={pricing} country={(selected.country as Country) || 'CA'} />

              {/* Funding */}
              <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
                    <Wallet className="h-4 w-4 text-indigo-600" />
                    {needsFunds ? 'Start your phone balance' : 'Top up your phone balance (optional)'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {needsFunds
                      ? 'Prepaid, like a phone plan: texts and calls come off this balance as you use them.'
                      : `You already have ${formatMoney(wallet.balanceCents)} on your balance.`}
                  </p>
                </div>
                <AmountPicker
                  value={initialAmountCents}
                  onChange={setInitialAmountCents}
                  min={needsFunds ? pricing.minTopupCents : 0}
                  max={pricing.maxTopupCents}
                  allowZero={!needsFunds}
                />
                <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  {card ? `Charged to ${card}` : 'You’ll add a card on the next screen (saved for auto top-up)'}
                </p>

                <div className="pt-3 border-t border-gray-100">
                  <AutoTopupForm
                    enabled={autoEnabled}
                    thresholdCents={autoThresholdCents}
                    amountCents={autoAmountCents}
                    onChange={(v) => { setAutoEnabled(v.enabled); setAutoThresholdCents(v.thresholdCents); setAutoAmountCents(v.amountCents); }}
                  />
                </div>
              </div>

              {selected.country === 'US' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    US carriers require business registration (A2P 10DLC) before texts from a US number are delivered
                    reliably. Calling works right away. Pick a Canadian number if you text mostly Canadian customers.
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleFundAndProvision}
                  disabled={isProvisioning}
                  className="flex-1 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {isProvisioning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      {provisionStep || 'Working…'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      {initialAmountCents > 0 ? `Add ${formatMoney(initialAmountCents)} & get this number` : 'Get this number'}
                    </>
                  )}
                </button>
                <button type="button" onClick={() => setSelected(null)} disabled={isProvisioning} className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700">
                  Back
                </button>
              </div>
            </div>
          ) : (
            /* ─────────── Picker ─────────── */
            <div className="space-y-5">
              <IntroCard pricing={pricing} />

              <div className="space-y-3">
                {/* Canada only for now — US requires A2P 10DLC registration before SMS works. */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 min-w-0">
                    <CityAutocompleteInput
                      value={cityText}
                      onChange={(v) => {
                        setCityText(v);
                        // Typing again invalidates the previously picked city.
                        if (city && v !== city.formatted) setCity(null);
                      }}
                      onCitySelected={(c) => {
                        setCity(c);
                        setCityText(c.formatted);
                      }}
                      countries={['ca']}
                      placeholder="City (e.g. Vancouver, BC)"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={areaCode}
                      onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      onKeyDown={(e) => { if (e.key === 'Enter') void handleSearch(false); }}
                      placeholder="Area code"
                      aria-label="Area code (optional)"
                      className="w-28 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => void handleSearch(false)}
                      disabled={isSearching}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <Search className="h-4 w-4" />
                      {isSearching ? 'Searching…' : hasSearched ? 'Search again' : 'Find numbers'}
                    </button>
                  </div>
                </div>

                {results.length > 0 && (
                  <>
                    {resultsCity && resultsMatchedBy && resultsMatchedBy !== 'city' && (
                      <p className="text-xs text-amber-700">
                        {resultsMatchedBy === 'near'
                          ? `No numbers are filed under ${resultsCity} right now — showing the nearest available numbers instead.`
                          : resultsMatchedBy === 'region'
                            ? `Nothing near ${resultsCity} right now — showing other numbers in the province.`
                            : `Nothing near ${resultsCity} right now — showing what's available${areaCode ? ` in ${areaCode}` : ' in Canada'}.`}
                      </p>
                    )}
                    {resultsCountry && resultsCountry !== country && (
                      <p className="text-xs text-amber-700">
                        That area code is in {COUNTRY_LABEL[resultsCountry as Country] || resultsCountry} — showing numbers from there.
                      </p>
                    )}
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {results.map((n) => (
                        <button
                          key={n.phoneNumber}
                          type="button"
                          onClick={() => setSelected(n)}
                          className="w-full text-left flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40 rounded-xl transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 font-mono">{formatNexusPhone(n.phoneNumber)}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {[n.locality, n.region].filter(Boolean).join(', ') || 'Local number'}
                              {!n.capabilities.sms ? ' · no texting' : ''}
                              {n.capabilities.mms ? ' · MMS' : ''}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs font-semibold text-indigo-600 inline-flex items-center gap-1">
                            Choose <ChevronRight className="h-3.5 w-3.5" />
                          </span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleSearch(true)}
                      disabled={isSearching}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${isSearching ? 'animate-spin' : ''}`} />
                      {isSearching ? 'Finding more…' : 'Show different numbers'}
                    </button>
                  </>
                )}

                {hasSearched && !isSearching && results.length === 0 && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
                    <p className="font-semibold text-gray-800">
                      No numbers available{resultsCity ? ` near ${resultsCity}` : ''}{areaCode ? ` in ${areaCode}` : ''} right now.
                    </p>
                    <p className="mt-0.5">
                      {areaCode
                        ? 'Big-city area codes sell out often — try clearing the area code, or use a nearby overlay code (for example 778 or 236 instead of 604, 647 or 437 instead of 416).'
                        : 'Try a nearby larger city, or leave the city blank to see everything available in Canada.'}
                    </p>
                  </div>
                )}

                {!hasSearched && (
                  <p className="text-xs text-gray-400">
                    Pick your city to see local numbers. Area code is optional. Canadian numbers only for now; all include texting (SMS/MMS) and calling.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────

function IntroCard({ pricing }: { pricing: NexusPhonePricing }) {
  return (
    <div className="p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Get a business number in one click</p>
          <p className="text-xs text-gray-600 mt-1">
            No carrier accounts, no setup. Pick a number and it's live for texting and calling across Nexus —
            Messages, the Dialler, automations and your AI receptionist.
          </p>
          <p className="text-xs text-gray-700 mt-2">
            Pay as you go from a prepaid balance: <span className="font-semibold">{formatMoney(pricing.numberMonthlyCents.CA)}/mo</span> for the
            number, texts from <span className="font-semibold">{formatUnitPrice(pricing.smsOutboundCents)}</span>, calls from{' '}
            <span className="font-semibold">{formatUnitPrice(pricing.voiceInboundPerMinCents)}/min</span>. Auto top-up keeps you running.
          </p>
        </div>
      </div>
    </div>
  );
}

function RatesCard({ pricing, country }: { pricing: NexusPhonePricing; country: Country }) {
  const monthly = pricing.numberMonthlyCents[country] ?? pricing.numberMonthlyCents.CA;
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1.5 text-sm">
      <p className="text-xs font-medium text-gray-500 mb-1">Pay-as-you-go rates (USD)</p>
      <Row label="Phone number" value={`${formatMoney(monthly)} / month`} />
      <Row label="Text sent" value={formatUnitPrice(pricing.smsOutboundCents)} />
      <Row label="Text received" value={formatUnitPrice(pricing.smsInboundCents)} />
      <Row label="Picture message sent" value={formatUnitPrice(pricing.mmsOutboundCents)} />
      <Row label="Outgoing call" value={`${formatUnitPrice(pricing.voiceOutboundPerMinCents)} / min`} />
      <Row label="Incoming call" value={`${formatUnitPrice(pricing.voiceInboundPerMinCents)} / min`} />
      <p className="text-[11px] text-gray-400 pt-1.5 border-t border-gray-200">
        Deducted from your phone balance as you use it. Long texts count as more than one segment; carrier fees and
        recordings are included at the same pay-as-you-go basis. No contracts — release the number any time.
      </p>
    </div>
  );
}

function AmountPicker({
  value,
  onChange,
  min,
  max,
  allowZero = false,
}: {
  value: number;
  onChange: (cents: number) => void;
  min: number;
  max: number;
  allowZero?: boolean;
}) {
  const presets = allowZero ? [0, ...TOPUP_PRESETS_CENTS] : TOPUP_PRESETS_CENTS;
  const isPreset = presets.includes(value);
  const [custom, setCustom] = useState(isPreset ? '' : String(value / 100));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {presets.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => { onChange(c); setCustom(''); }}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${value === c && custom === '' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-300'}`}
          >
            {c === 0 ? 'Skip for now' : formatMoney(c)}
          </button>
        ))}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
          <input
            type="number"
            inputMode="decimal"
            min={min / 100}
            max={max / 100}
            step="5"
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              const n = Math.round(Number(e.target.value) * 100);
              if (Number.isFinite(n) && n > 0) onChange(n);
            }}
            placeholder="Other"
            className="w-28 bg-white border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
      {min > 0 && <p className="text-[11px] text-gray-400">Minimum {formatMoney(min)} · maximum {formatMoney(max)}</p>}
    </div>
  );
}

function AutoTopupForm({
  enabled,
  thresholdCents,
  amountCents,
  onChange,
}: {
  enabled: boolean;
  thresholdCents: number;
  amountCents: number;
  onChange: (v: { enabled: boolean; thresholdCents: number; amountCents: number }) => void;
}) {
  const thresholdOptions = THRESHOLD_OPTIONS_CENTS.includes(thresholdCents) ? THRESHOLD_OPTIONS_CENTS : [...THRESHOLD_OPTIONS_CENTS, thresholdCents].sort((a, b) => a - b);
  const amountOptions = AUTO_AMOUNT_OPTIONS_CENTS.includes(amountCents) ? AUTO_AMOUNT_OPTIONS_CENTS : [...AUTO_AMOUNT_OPTIONS_CENTS, amountCents].sort((a, b) => a - b);
  const select = 'bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500';
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange({ enabled: e.target.checked, thresholdCents, amountCents })}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm font-medium text-gray-800">Auto top-up</span>
        <span className="text-xs text-gray-400">(recommended)</span>
      </label>
      {enabled ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 pl-6">
          <span>When my balance drops below</span>
          <select value={thresholdCents} onChange={(e) => onChange({ enabled, thresholdCents: Number(e.target.value), amountCents })} className={select}>
            {thresholdOptions.map((c) => <option key={c} value={c}>{formatMoney(c)}</option>)}
          </select>
          <span>add</span>
          <select value={amountCents} onChange={(e) => onChange({ enabled, thresholdCents, amountCents: Number(e.target.value) })} className={select}>
            {amountOptions.map((c) => <option key={c} value={c}>{formatMoney(c)}</option>)}
          </select>
        </div>
      ) : (
        <p className="text-xs text-amber-700 pl-6 inline-flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Without auto top-up, texting and calling stop the moment your balance reaches $0.
        </p>
      )}
    </div>
  );
}

